import { Form } from 'antd';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { withForm } from 'react-antd-formutil';
import { useParams } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import FieldSelect from 'components/FieldSelect';
import Loading from 'components/Loading';
import SwitchContributionWay from 'modules/Deposit/components/SwitchContributionWay';
import TaxLimit from 'modules/Deposit/components/TaxLimit';
import { RecommendDepositEnum } from 'modules/Deposit/type';
import security from 'stores/security';
import { AccountCustomerTypeEnum } from 'types/user';
import { getCurrencyInfo } from 'utils/util';
import { getBankIcon, getBankName, TransferDepositCurrency } from '../helper';
import { useChoiceList } from '../request';
import BankInfo from './BankInfo';
import { parseBankInfo } from './helper';
import './style.scss';

interface RemittanceProps {
    defaultCurrency: TransferDepositCurrency;
    isIRA?: boolean;
}

const Remittance: React.FC<RemittanceProps> = ({ defaultCurrency, isIRA }) => {
    const params = useParams<{
        bankId?: string;
    }>();
    const [bankId, setBankId] = useState('');
    const [currency, setCurrency] = useState<string>(isIRA ? 'USD' : defaultCurrency);
    const {
        isLoading: isLoadingChoiceList,
        error: errorChoiceList,
        data: dataChoiceList,
        mutate: mutateChoiceList
    } = useChoiceList();

    const { depositBankList, allBankList, currencyList } = useMemo(() => {
        if (dataChoiceList) {
            const list = Object.keys(dataChoiceList);

            const res = parseBankInfo(dataChoiceList, currency, String(bankId || params.bankId));

            window.history.replaceState(null, '', `/profile/deposit/${res.currency}/${res.id}`);

            setBankId(res.id);
            setCurrency(res.currency);

            return {
                currencyList: list.map(item => {
                    const currencyInfo = getCurrencyInfo(item);

                    return {
                        value: item,
                        label: currencyInfo.name,
                        icon: currencyInfo.icon,
                        key: currencyInfo.name
                    };
                }),
                ...res
            };
        }

        return {
            id: '',
            depositBankList: [],
            allBankList: [],
            currencyList: []
        };
    }, [dataChoiceList, bankId, currency, params.bankId]);

    const renderCurrencySelect = () => {
        return (
            <div className="step-box">
                <h3 className="step-title">
                    <span className="step">1</span>
                    {__('汇款币种')}
                </h3>
                <div className="comp-form-item">
                    <FieldSelect
                        value={currency}
                        data={currencyList}
                        renderInComponent
                        optionLabelProp="key"
                        disabled={isIRA}
                        onChange={currency => {
                            setCurrency(currency);
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderBankSelect = () => {
        const currentData = allBankList.find(item => String(item.id) === String(bankId));

        if (!currentData) {
            return null;
        }

        const list = depositBankList.map(item => ({
            value: item.id,
            label: getBankName(item.key),
            icon: getBankIcon(item.key),
            key: getBankName(item.key)
        }));

        return (
            <div className="step-box">
                <h3 className="step-title">
                    <span className="step">2</span>
                    {__('汇款银行')}
                </h3>
                <div className="comp-form-item">
                    <FieldSelect
                        value={currentData.id}
                        data={list}
                        renderInComponent
                        optionLabelProp="key"
                        onChange={id => {
                            setBankId(id);
                        }}
                    />
                </div>
            </div>
        );
    };

    const render = () => {
        if (isLoadingChoiceList) {
            return <Loading />;
        }

        if (errorChoiceList) {
            return <ErrorBox error={errorChoiceList} onClick={() => mutateChoiceList()} />;
        }

        return (
            <Form layout="vertical">
                <TaxLimit />
                {renderCurrencySelect()}
                {renderBankSelect()}
                {!!bankId && <BankInfo currency={currency} bankId={bankId} />}
            </Form>
        );
    };

    const getTitle = () => {
        switch (security.currentAccount!.customerType) {
            case AccountCustomerTypeEnum.IRA_ROTH:
                return __('Contribute to your Roth IRA via wire');
            case AccountCustomerTypeEnum.IRA_TRADITIONAL:
                return __('Contribute to your Traditional IRA via wire');
            case AccountCustomerTypeEnum.IRA_ROLLOVER:
                return __('Contribute to your Rollover IRA via wire');
            default:
                return __('通过转账汇款入金');
        }
    };

    return (
        <div className="deposit-remittance-root">
            <h3 className="main-title">
                <span>{getTitle()}</span>
                <SwitchContributionWay currentWay={RecommendDepositEnum.Transfer} />
            </h3>
            {render()}
        </div>
    );
};

export default withForm(Remittance);
